import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ApiConstants from '../utils/ApiConstants';
import { useOutsideClick, isDebugMode } from "../utils/utils";
import { Link } from "react-router-dom";
import { useLayoutLanguage } from '../context/LayoutLanguageContext';
import { useAuthenticatedApiFetch } from '../hooks/useAuthenticatedApiFetch';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from '../authConfig';
import MoneyDisplay from '../components/MoneyDisplay';
import { useAppNavigation } from "../hooks/useAppNavigation";
import { APP_ROUTES } from "../hooks/useAppNavigation";
import { useSimulation } from "../context/SimulationContext";

const padNumber = (num) => String(num).padStart(2, "0");

const renderDate = (dateString, separator = ' <br />') => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return `${d.toISOString().split("T")[0]}${separator}${padNumber(d.getHours())}:${padNumber(d.getMinutes())}`;
};

const FilterSortMenu = ({
  showFilterMenu, onFilterButtonClick, filterMenuRef,
  showSortMenu, onSortButtonClick, sortMenuRef,
  statuses, selectedFilters, onFilterOptionClick,
  onSortOptionClick, orderBy, onClear, layoutT,
}) => (
  <>
    <div className='filter'>
      <button onClick={onFilterButtonClick}>
        <i className="fa-regular fa-filter"></i> {layoutT('filter')}
      </button>
      {showFilterMenu && (
        <div ref={filterMenuRef} className={`applications-dropdown-menu ${showFilterMenu ? 'show' : ''}`}>
          {statuses.map((status, index) => (
            <div
              key={index}
              tabIndex="0"
              onClick={() => onFilterOptionClick(status.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onFilterOptionClick(status.value); } }}
            >
              {status.display}
              {selectedFilters.includes(status.value) && (
                <i className="fa-solid fa-check"></i>
              )}
            </div>
          ))}
        </div>
      )}
    </div>

    <div className='sort'>
      <button onClick={onSortButtonClick}>
        <i className="fa-solid fa-arrow-down-wide-short"></i> {layoutT('sort')}
      </button>
      {showSortMenu && (
        <div ref={sortMenuRef} className={`applications-dropdown-menu ${showSortMenu ? 'show' : ''}`}>
          <div
            tabIndex='0'
            onClick={() => onSortOptionClick("date_recent")}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSortOptionClick("date_recent"); } }}
          >
            <i className={`fa-solid fa-check ${orderBy === "date_recent" ? '' : 'invisible'}`}></i>
            Date <span>({layoutT('date_recent')})</span>
          </div>
          <div
            tabIndex='0'
            onClick={() => onSortOptionClick("date_oldest")}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSortOptionClick("date_oldest"); } }}
          >
            <i className={`fa-solid fa-check ${orderBy === "date_oldest" ? '' : 'invisible'}`}></i>
            Date <span>({layoutT('date_oldest')})</span>
          </div>
        </div>
      )}
    </div>

    <div className='clear'>
      <button onClick={onClear}>
        <i className="fa-solid fa-arrow-rotate-left"></i> {layoutT('clear')}
      </button>
    </div>
  </>
);


const Applications = () => {

  const { instance, accounts } = useMsal();
  const authenticatedApiFetch = useAuthenticatedApiFetch(instance);
  const isAuthenticated = useIsAuthenticated();
  const { goToError, goToUnauthorized } = useAppNavigation();

  useEffect(() => {
    // Check if authentication status is fully loaded
    if (isAuthenticated === false && accounts.length === 0) {
      instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, accounts, instance]);


  const { layoutT, layoutLanguage } = useLayoutLanguage();
  const currentLanguage = layoutLanguage === 'English' ? 'en-CA' : 'fr-CA';


  const navigate = useNavigate();

  const [selectedApps, setSelectedApps] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewApplicationModal, setShowNewApplicationModal] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const selectedAppIds = Object.keys(selectedApps).filter(appId => selectedApps[appId]);
  const [tableOpacity, setTableOpacity] = useState(1);

  const [isLoading, setIsLoading] = useState(true);

  const [loadError, setLoadError] = useState(null);

  const { isSimulation, setSimulation } = useSimulation();

  const [statuses, setStatuses] = useState([]);


  const [expandedFlows, setExpandedFlows] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  const handleNewApplication = async (saleFlowId, simulationFlag = false) => {
    setIsLoading(true);

    const endpoint = ApiConstants.getCreateApplicationEndpoint(saleFlowId);
    const url = `${ApiConstants.BASE_URL}${endpoint}`;

    try {
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'simulation': simulationFlag,
        },
      };

      const result = await authenticatedApiFetch(url, options, 'json', currentLanguage, instance);

      if (!result) {
        console.warn('Result is null so likely unauthorized');
        goToUnauthorized();
        return;
      }

      const status = result.status || result?.response?.status;
      if (status === 500) {
        goToError();
        return;
      }

      const data = result.data;

      if (data?.clientId) {
        navigate(`${APP_ROUTES.PRODUCT}?product=${data.saleFlowId}&client=${data.clientId}`);
      } else {
        console.error('Client ID not found in API response', data);
        goToError();
      }

    } catch (error) {
      console.error('Error creating new client', error);

      const status = error?.status || error?.response?.status;

      if (status === 401) {
        goToUnauthorized();
      } else {
        goToError();
      }

    } finally {
      setIsLoading(false);
    }
  };


  const [data, setData] = useState(null);

  const [searchInput, setSearchInput] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [orderBy, setOrderBy] = useState("date_recent");
  const [simulationInfo, setSimulationInfo] = useState(null);

  const fetchSimulationInfo = async () => {
    try {
      const url = `${ApiConstants.BASE_URL}${ApiConstants.getSimulationInfo()}`;
      const result = await authenticatedApiFetch(url, {}, 'json', currentLanguage, instance);

      if (result?.status === 200 && result.data) {
        setSimulationInfo(result.data);
        return result.data;
      }
    } catch (error) {
      console.error("Erreur simulation info:", error);
      setLoadError("Failed to load data. Please try again later.");
      goToError();
      return null;
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchSimulationInfo();
    return () => controller.abort();
  }, [refreshTrigger, currentLanguage, instance]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [fadeIn, setFadeIn] = useState(false);

  const handleFadeIn = () => {
    setFadeIn(false);
    setTimeout(() => {
      setFadeIn(true);
    }, 10);
  };

  const handlePageChange = (page) => {
    handleFadeIn();
    setCurrentPage(page);
  };

  const getFilteredData = () => {
    if (!data || !Array.isArray(data.flowPropositions)) return [];
    return data.flowPropositions;
  };

  const handleItemsPerPageChange = (e) => {
    handleFadeIn();

    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset current page to 1 when changing items per page
  };

  const handleOrderByChange = (newOrder) => {
    handleFadeIn();
    setOrderBy(newOrder);
    setCurrentPage(1);
  };


  const handleSearch = (e) => {
    handleFadeIn();
    setSearchInput(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
    }, 500);

    return () => clearTimeout(t);
  }, [searchInput]);


  const handleFilter = (newFilter) => {
    handleFadeIn();
  };

  const toggleFlow = (flowId) => {
    setExpandedFlows((prev) => ({
      ...prev,
      [flowId]: !prev[flowId],
    }));
  };

  const toggleAllFlows = () => {
    const newExpandedState = {};
    const shouldExpand = !allExpanded;

    if (shouldExpand) {
      data.flowPropositions.forEach((flow) => {
        newExpandedState[flow.id] = true;
      });
    }

    setExpandedFlows(newExpandedState);
    setAllExpanded(shouldExpand);
  };

  const goToClient = (clientId, saleFlowId) => {
    navigate(`${APP_ROUTES.PRODUCT}?product=${saleFlowId}&client=${clientId}`);
  };

  const renderRows = () => {
    const filteredData = getFilteredData();
    if (filteredData.length === 0) return null;

    return filteredData.flatMap((flow) => {


      const isExpanded = expandedFlows[flow.id];


      const totalFlowPremium = flow.mainPropositions.reduce((acc, main) => {
        const mainPremium = parseFloat(main.premium) || 0;

        const secondaryPremiums =
          main.secondaryPropositions?.reduce((subAcc, sec) => {
            return subAcc + (parseFloat(sec.premium) || 0);
          }, 0) || 0;

        return acc + mainPremium + secondaryPremiums;
      }, 0);


      return (
        <React.Fragment key={`flow-${flow.id}`}>
          <tr className="flow-wrapper">
            <td colSpan="6" className="flow-wrapper-td">
              <div className="flow-container">
                {/* Left side - main content */}
                <div className="flow-left">
                  <table className="applications-inner-table">
                    <tbody>
                      <tr className={flow.isDisabled ? "disabled-application flow-prop" : "flow-prop"}>
                        <td>
                          <div className="applications-checkbox">
                            <div className="applications-client-checkbox">
                              <input
                                id={`checkbox-${flow.clientId}`}
                                type="checkbox"
                                checked={!!selectedApps[flow.clientId]}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    { clientId: flow.clientId, saleFlowId: flow.saleFlowId },
                                    e
                                  )
                                }
                                className="form-check-input"
                                disabled={flow.isDisabled}
                              />
                            </div>
                            <div className="applications-client" style={{ flex: 1 }}>
                              <div className="applications-table-colapse">

                                <button
                                  onClick={() => toggleFlow(flow.id)}
                                  className="expand-btn"
                                >
                                  {isExpanded ? (
                                    <i className="fa-solid fa-chevron-up"></i>
                                  ) : (
                                    <i className="fa-solid fa-chevron-down"></i>
                                  )}
                                </button>

                              </div>

                              <div className="applications-table-colapse">

                                {flow.isDisabled ? (
                                  <span>
                                    {flow.lastName} {flow.firstName}
                                  </span>
                                ) : (
                                  <Link
                                    to={`${APP_ROUTES.PRODUCT}?product=${flow.saleFlowId}&client=${flow.clientId}`}
                                  >
                                    {flow.lastName} {flow.firstName}
                                  </Link>
                                )}
                              </div>

                              {isDebugMode() && flow.version ? (
                                <div className="app-version">version: {flow.version}</div>
                              ) : null}
                            </div>
                          </div>
                        </td>

                        <td>
                          <MoneyDisplay amount={totalFlowPremium} layoutLanguage={layoutLanguage} />
                        </td>

                        <td className="applications-table-colapse-plus">
                          <div className="gradient-icon">

                            <button
                              onClick={() => toggleFlow(flow.id)}
                              className="expand-btn"
                            >
                              {isExpanded ? (
                                <i className="fa-solid fa-square-minus"></i>
                              ) : (
                                <i className="fa-solid fa-square-plus"></i>
                              )}
                            </button>

                          </div>
                        </td>

                        <td dangerouslySetInnerHTML={{ __html: renderDate(flow.last_modified) }} />

                        <td>{flow.statusDisplay ?? flow.mainPropositions[0]?.statusDisplay ?? ""}<br /><span className="clientToProductId">{flow.clientToProductId}</span></td>
                      </tr>

                      {/* Expanded rows */}
                      {isExpanded &&
                        flow.mainPropositions.map((main) => (
                          <React.Fragment key={`main-${main.id}`}>
                            <tr className={`main-prop ${main.isDisabled ? "disabled-application" : ""}`}>
                              <td>


                                <div> {/* Indent for hierarchy */}

                                  <div className="applications-client">
                                    {main.isDisabled ? (
                                      <span>{main.productName}</span>
                                    ) : (
                                      <Link
                                        to={`${APP_ROUTES.PRODUCT}?product=${main.saleFlowId}&client=${main.clientId}`}
                                      >
                                        {main.productName}
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <MoneyDisplay
                                  amount={main.premium ?? ""}
                                  layoutLanguage={layoutLanguage}
                                />
                              </td>
                              <td>
                                <MoneyDisplay
                                  amount={main.insuranceAmount}
                                  layoutLanguage={layoutLanguage}
                                />
                                <br />
                                {main.term}
                              </td>
                              <td /*dangerouslySetInnerHTML={{ __html: renderDate(main.last_modified) }} */ >
                              </td>
                              <td>
                                {main.policyId && (
                                  <>
                                    <br />
                                    <span>({main.policyId})</span>
                                  </>
                                )}
                              </td>
                            </tr>

                            {main.secondaryPropositions?.length > 0 &&
                              main.secondaryPropositions.map((sec) => (
                                <tr
                                  key={`sec-${sec.id}`}
                                  className={`secondary-prop ${sec.isDisabled ? "disabled-application" : ""}`}
                                >
                                  <td>
                                    <div> {/* More indent for secondary */}
                                      <div className="applications-client">
                                        {sec.isDisabled ? (
                                          <span>{sec.productName}</span>
                                        ) : (
                                          <Link
                                            to={`${APP_ROUTES.PRODUCT}?product=${sec.saleFlowId}&client=${sec.clientId}`}
                                          >
                                            {sec.productName}
                                          </Link>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <MoneyDisplay
                                      amount={sec.premium ?? ""}
                                      layoutLanguage={layoutLanguage}
                                    />
                                  </td>
                                  <td>
                                    <MoneyDisplay
                                      amount={sec.insuranceAmount}
                                      layoutLanguage={layoutLanguage}
                                    />
                                    <br />
                                    {sec.term}
                                  </td>
                                  <td /*dangerouslySetInnerHTML={{ __html: renderDate(sec.last_modified) }} */ >
                                  </td>
                                  <td>
                                    {sec.policyId && (
                                      <>
                                        <br />
                                        <span>({sec.policyId})</span>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </React.Fragment>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Right side - Open button or Disabled*/}
                <div className="flow-right">
                  {flow.isDisabled ? (
                    <div>
                      <div className="aplication-disabled"><i className="fa-regular fa-ban"></i></div>
                    </div>
                  ) : (
                    <Link
                      to={`${APP_ROUTES.PRODUCT}?product=${flow.saleFlowId}&client=${flow.clientId}`}
                      className="applications-client-right"
                    >
                      <div className="open">{layoutT("open")}</div>
                    </Link>
                  )}
                </div>

              </div>
            </td>
          </tr>
        </React.Fragment>
      );
    });
  };

  const renderRowsMobile = () => {
    const filteredData = getFilteredData();
    if (filteredData.length === 0) return null;

    return filteredData.flatMap((flow) => {

      const totalFlowPremium = flow.mainPropositions.reduce((acc, main) => {
        const mainPremium = parseFloat(main.premium) || 0;
        const secondaryPremiums =
          main.secondaryPropositions?.reduce(
            (subAcc, sec) => subAcc + (parseFloat(sec.premium) || 0),
            0
          ) || 0;
        return acc + mainPremium + secondaryPremiums;
      }, 0);

      // Render flow container
      const flowContainer = (
        <div
          key={`flow-${flow.id}`}
          className={flow.isDisabled ? "applications-mobile disabled-application" : "applications-mobile"}
        >
          <div className="applications-mobile-top d-flex justify-content-between">
            <div className="applications-client">
              {flow.isDisabled ? (
                <span>
                  {flow.lastName}, <br />
                  {flow.firstName}
                </span>
              ) : (
                <Link
                  to={`${APP_ROUTES.PRODUCT}?product=${flow.saleFlowId}&client=${flow.clientId}`}
                >
                  {flow.lastName}, <br />
                  {flow.firstName}
                </Link>
              )}
            </div>
            <div className="applications-amount">
              <MoneyDisplay amount={totalFlowPremium} layoutLanguage={layoutLanguage} />
            </div>
          </div>

          <div className="applications-mobile-middle d-flex justify-content-between">
            <div dangerouslySetInnerHTML={{ __html: `<span>${layoutT("status")}: </span>${flow.statusDisplay}<br/><span>${layoutT("last_modified")}: </span>${renderDate(flow.last_modified, ' ')}` }} />
          </div>

          <div className="applications-mobile-bottom d-flex">
            <div className="applications-client-checkbox">
              <label>
                <input
                  id={`checkbox-${flow.clientId}-mobile`}
                  type="checkbox"
                  checked={!!selectedApps[flow.clientId]}
                  onChange={(e) =>
                    handleCheckboxChange(
                      { clientId: flow.clientId, saleFlowId: flow.saleFlowId },
                      e
                    )
                  }
                  className="form-check-input"
                  disabled={flow.isDisabled}
                />
                <span>Select</span>
              </label>
            </div>
            <div className="applications-goto-client">
              <button
                className="btn-purple"
                onClick={() => goToClient(flow.clientId, flow.saleFlowId)}
              >
                <i className="fa-regular fa-folder"></i> {layoutT("open")}
              </button>
            </div>
          </div>
        </div>
      );

      // Include mainPropositions and secondaryPropositions under this flow
      const expandedItems = flow.mainPropositions.flatMap((main) => {
        const mainPremium = parseFloat(main.premium) || 0;
        const secondaryTotal = main.secondaryPropositions?.reduce(
          (subAcc, sec) => subAcc + (parseFloat(sec.premium) || 0),
          0
        ) || 0;

        const mainRow = (
          <div key={`main-${main.id}`} className="applications-mobile-sub">

          </div>
        );

        const secondaryRows = main.secondaryPropositions?.map((sec) => (
          <div
            key={`sec-${sec.id}`}
            className="applications-mobile-sub-secondary"
          >
            <div className="applications-client">
              {sec.isDisabled ? (
                <span>{sec.productName}</span>
              ) : (
                <Link
                  to={`${APP_ROUTES.PRODUCT}?product=${sec.saleFlowId}&client=${sec.clientId}`}
                >
                  {sec.productName}
                </Link>
              )}
            </div>
            <div className="applications-amount">
              <MoneyDisplay amount={parseFloat(sec.premium) || 0} layoutLanguage={layoutLanguage} />
            </div>
          </div>
        ));

        return [mainRow, ...(secondaryRows || [])];
      });

      return [flowContainer, ...expandedItems];
    });
  };


  const renderPagination = () => {
    const totalPagesFromApi = Number(data?.total_pages);
    const totalResultsFromApi = Number(data?.total_results);

    // fallback
    const fallbackTotalPages =
      totalResultsFromApi
        ? Math.ceil(totalResultsFromApi / itemsPerPage)
        : (getFilteredData().length ? 1 : 1);

    const totalPages = Number.isFinite(totalPagesFromApi) && totalPagesFromApi > 0
      ? totalPagesFromApi
      : fallbackTotalPages;


    return (
      <div className="pagination-controls">
        <button
          aria-label="Go to first page"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <i className="fa-solid fa-angles-left"></i>
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <span>
          {currentPage}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <i className="fa-solid fa-chevron-right"></i>
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <i className="fa-solid fa-angles-right"></i>
        </button>
      </div>
    );
  };

  const [showFilterMenuTop, setShowFilterMenuTop] = useState(false);
  const [showSortMenuTop, setShowSortMenuTop] = useState(false);

  const [showFilterMenuBottom, setShowFilterMenuBottom] = useState(false);
  const [showSortMenuBottom, setShowSortMenuBottom] = useState(false);

  const filterMenuTopRef = useRef();
  const sortMenuTopRef = useRef();

  const filterMenuBottomRef = useRef();
  const sortMenuBottomRef = useRef();

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedSort, setSelectedSort] = useState('');

  useOutsideClick(filterMenuTopRef, () => setShowFilterMenuTop(false));
  useOutsideClick(sortMenuTopRef, () => setShowSortMenuTop(false));
  useOutsideClick(filterMenuBottomRef, () => setShowFilterMenuBottom(false));
  useOutsideClick(sortMenuBottomRef, () => setShowSortMenuBottom(false));


  const handleFilterOptionClick = (value) => {
    let updatedFilters;

    if (selectedFilters.includes(value)) {
      updatedFilters = selectedFilters.filter((f) => f !== value);
    } else {
      updatedFilters = [...selectedFilters, value];
    }

    setSelectedFilters(updatedFilters);
    setCurrentPage(1);

    handleFilter(value);

    setShowFilterMenuTop(false);
    setShowFilterMenuBottom(false);
  };


  const handleSortOptionClick = (value) => {

    setSelectedSort(value);

    handleOrderByChange(value);
    setShowSortMenuTop(false);
    setShowSortMenuBottom(false);
  };

  const handleFilterButtonClickTop = () => {
    setShowFilterMenuTop(!showFilterMenuTop);
  };

  const handleSortButtonClickTop = () => {
    setShowSortMenuTop(!showSortMenuTop);
  };

  const handleFilterButtonClickBottom = () => {
    setShowFilterMenuBottom(!showFilterMenuBottom);
  };

  const handleSortButtonClickBottom = () => {
    setShowSortMenuBottom(!showSortMenuBottom);
  };

  const removeFilter = (filterToRemove) => {
    const updatedFilters = selectedFilters.filter((filter) => filter !== filterToRemove);
    setSelectedFilters(updatedFilters);

  };

  const clearAll = () => {
    setSelectedFilters([]);
    setSelectedSort('');
    setSearchInput("");
    setOrderBy("date_recent");
    setCurrentPage(1);
  };

  const clearSort = () => {
    setSelectedSort('');
    handleOrderByChange('');
  };


  // DELETE APPLICATIONS

  const handleCheckboxChange = (idsObject, e) => {
    const { clientId, saleFlowId } = idsObject;
    const isChecked = e.target.checked;

    setSelectedApps(prev => {
      const updatedSelectedApps = { ...prev };
      updatedSelectedApps[clientId] = isChecked ? { saleFlowId, isChecked } : undefined;
      return updatedSelectedApps;
    });
  };

  const [loadingDelete, setLoadingDelete] = useState(false);

  const deleteSelectedApps = async () => {
    setLoadingDelete(true);

    try {
      // Filter selectedAppIds to only include entries where isChecked is true and selectedApps[clientId] is defined
      const selectedAppIds = Object.keys(selectedApps).filter(clientId => selectedApps[clientId]?.isChecked);


      for (const clientId of selectedAppIds) {
        // Check if selectedApps[clientId] is defined
        if (selectedApps[clientId]) {
          const saleFlowId = selectedApps[clientId].saleFlowId;
          const deleteEndpoint = ApiConstants.getClientProductDeleteEndpoint(saleFlowId, clientId);
          const url = `${ApiConstants.BASE_URL}${deleteEndpoint}`;

          const result = await authenticatedApiFetch(url, {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
          }, 'json',
            currentLanguage,
            instance);

          if (result.status !== 200) {
            // Handle unsuccessful delete operation
            console.error(`Error deleting application with clientId ${clientId}: Status code ${result.status}`);
          }
        }
      }

      setShowDeleteModal(false);
      setSelectedApps({});
      setTableOpacity(0);

      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
        setTableOpacity(1);
      }, 300);

    } catch (error) {
      console.error("Error deleting applications:", error);
    }

    setLoadingDelete(false);
  };

  const [products, setProducts] = useState([]);

  const getProducts = async () => {
    const endpoint = ApiConstants.getProducts();

    try {
      const url = `${ApiConstants.BASE_URL}${endpoint}`;
      const result = await authenticatedApiFetch(url, { method: 'GET' }, 'json', currentLanguage, instance);

      setProducts(result.data);
    } catch (error) {
      console.error('Error making GET request:', error);
      goToError();
    }
  };

  useEffect(() => {
    getProducts();
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const params = new URLSearchParams();

        // API expects 0-based page_nr
        params.set("page_nr", String(Math.max(0, currentPage - 1)));
        params.set("page_limit", String(itemsPerPage));

        const q = (debouncedSearch || "").trim();
        if (q) params.set("search", q);

        // sort mapping: UI (date_recent/date_oldest) -> API (order_by/order_type)
        params.set("order_by", "last_modified");
        params.set("order_type", orderBy === "date_oldest" ? "asc" : "desc");

        if (selectedFilters?.length > 0) {
          params.set("filterBy", "status");
          params.set("filterValue", selectedFilters.join(",")); // Completed,Expired,Etc..
        }

        const url = `${ApiConstants.BASE_URL}${ApiConstants.PROPOSITION_CONTROLLER}?${params.toString()}`;
        const result = await authenticatedApiFetch(url, { method: "GET" }, "json", currentLanguage, instance);

        if (result?.status === 200 && result.data) {
          setData(result.data);
          setStatuses(result.data.statuses || []);
          setLoadError(null);
        } else {
          setLoadError("Failed to fetch data. Please try again later.");
          setData(null);
        }
      } catch (error) {
        console.error("An error occurred while fetching data:", error);
        setLoadError("Failed to load data. Please try again later.");
        goToError();
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    refreshTrigger,
    currentLanguage,
    instance,
    currentPage,
    itemsPerPage,
    debouncedSearch,
    orderBy,
    selectedFilters
  ]);

  useEffect(() => {
    document.title = layoutT('applications_management');
  }, [layoutT]);

  return (

    <div className="content-maximize">
      <div id="applications-page">
        {isLoading ? (
          <div className='loading-wrapper-maximize'><div className='loading'>Loading...</div></div>
        ) : loadError ? (
          <div className="error">Error: {loadError}</div>
        ) : (
        <div className="form-q">
            <h1>{layoutT('applications_management')}</h1>

            <div>

              <div className="application-buttons">
                <div className='new-prop-btn '>
                  <button
                    className="btn-purple new-application form-control"
                    onClick={() => {
                      const simulationFlag = false;
                      setSimulation(simulationFlag);

                      if (isDebugMode()) {
                        setShowNewApplicationModal(true);
                      } else {
                        handleNewApplication(10, false);
                      }
                    }}
                  >
                    <i className="fa-solid fa-plus"></i> {layoutT('new_application')}
                  </button>


                </div>

                <div className='drop search '>
                  <input
                    type="text"
                    className="form-control"
                    value={searchInput}
                    onChange={handleSearch}
                    placeholder={layoutT('search_by_name')}
                    name='search'
                  />
                </div>
              </div>

              <div className="filters">


                <div className="pagination-left">

                  <div className='pagination'>
                    {renderPagination()}
                  </div>

                  <div className='pagination-select '>

                    <div className='filter-select applications-number'>
                      <select
                        id="itemsPerPage-top"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="itemsPerPage">{layoutT('per_page')} </label>
                    </div>

                  </div>

                </div>

                <div className="filters-right" >
                  <FilterSortMenu
                    showFilterMenu={showFilterMenuTop}
                    onFilterButtonClick={handleFilterButtonClickTop}
                    filterMenuRef={filterMenuTopRef}
                    showSortMenu={showSortMenuTop}
                    onSortButtonClick={handleSortButtonClickTop}
                    sortMenuRef={sortMenuTopRef}
                    statuses={statuses}
                    selectedFilters={selectedFilters}
                    onFilterOptionClick={handleFilterOptionClick}
                    onSortOptionClick={handleSortOptionClick}
                    orderBy={orderBy}
                    onClear={clearAll}
                    layoutT={layoutT}
                  />

                </div>


              </div>




              <div className="selected-filters">


                {selectedFilters.map((filter, index) => {
                  const status = statuses.find((status) => status.value === filter);

                  return (
                    <button key={index} className="filter-button">
                      <span onClick={() => removeFilter(filter)}>
                        <i className="fa-solid fa-circle-xmark"></i>
                      </span>
                      <i className="fa-regular fa-filter"></i> {status ? status.display : filter}
                    </button>
                  );
                })}

                {selectedSort && (
                  <button className="sort-button">
                    <span onClick={clearSort}><i className="fa-solid fa-circle-xmark"></i></span> <i className="fa-solid fa-arrow-down-wide-short"></i> Date {layoutT(selectedSort)}
                  </button>
                )}
              </div>

            </div>
            <div>



              <table className="applications-table" style={{ opacity: tableOpacity }}>

                <thead><tr><th>

                  <div className="applications-table-colapse">


                    {renderRows() !== null && (
                      <div className="applications-table-colapse-button">

                        <div className="gradient-icon">
                          <button className="application-expand" onClick={toggleAllFlows}>
                            {allExpanded ? <i className="fa-solid fa-square-minus"></i> : <i className="fa-solid fa-square-plus"></i>}
                          </button>
                        </div>
                      </div>
                    )}



                    <div className="applications-table-colapse-name">
                      {layoutT('name')}
                    </div>
                  </div>


                </th>
                  {/*<th>{layoutT('product')}</th>*/}
                  <th>{layoutT('premium')}</th><th>{layoutT('insured_amount')}<br />{layoutT('term')}</th><th>{layoutT('last_modified')}</th><th>{layoutT('status')}</th></tr></thead>
                <tbody className={fadeIn ? "fade-in" : ""}>{renderRows()}</tbody>
              </table>


              <div className="applications-table-mobile" style={{ opacity: tableOpacity }}>

                <div className={fadeIn ? "fade-in" : ""}>{renderRowsMobile()}</div>

              </div>


              {/* Delete applications modal */}

              <div
                className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`}
                id="deleteModal"
                tabIndex="-1"
                aria-hidden="true"
              >

                <div className="modal-dialog deletion">
                  <div className="modal-content">
                    <div className="d-flex">
                      <div className="circle-exclamation"><i className="fa-regular fa-circle-exclamation"></i></div>
                      <div className="modal-to-right">

                        <div className="modal-header">
                          <h4 className="modal-title">
                            <br />{layoutT('delete_applications')}</h4>
                          <button type="button" className="close" onClick={() => setShowDeleteModal(false)}><i className="fa-solid fa-circle-xmark"></i></button>
                        </div>
                        <div className="modal-body">

                          {selectedAppIds.length > 0
                            ? `${layoutT('are_you_sure_you_want_to_delete_the_selected')} ${selectedAppIds.length} ${selectedAppIds.length === 1 ? 'application' : 'applications'} ? ${layoutT('you_wont_retrieve_it_afterwards')}.`
                            : layoutT('please_select_at_least_one_application_to_delete')
                          }
                        </div>


                        {selectedAppIds.length > 0 && (
                          <div className="text-center">
                            <button type="button" className="btn-purple purple" onClick={() => setShowDeleteModal(false)}>{layoutT('cancel')}</button>
                            <button type="button" className="btn-purple red position-relative" onClick={deleteSelectedApps}>{layoutT('delete')}
                              {loadingDelete && <div className="loading-next"><div className="loading"></div></div>}

                            </button>
                          </div>
                        )}

                      </div>
                    </div>

                  </div>
                </div>
              </div>


              <div
                className={`modal fade ${showNewApplicationModal ? 'show d-block' : ''}`}
                id="newApplicationModal"
                tabIndex="-1"
                aria-hidden="true"
              >
                <div className="modal-dialog creation">
                  <div className="modal-content text-center">
                    <div className="modal-header text-center">
                      <h4 className="modal-title">

                        {!isSimulation && (
                          <>
                            <i className="fa-solid fa-file-circle-plus"></i> <br />
                            {layoutT('create_new_application')}
                          </>
                        )}

                        {isSimulation && (
                          <>
                            <i className="fa-solid fa-file-circle-info"></i> <br />
                            {layoutT('create_new_simulation')}
                          </>
                        )}


                      </h4>
                      <button type="button" className="close" onClick={() => setShowNewApplicationModal(false)}><i className="fa-solid fa-circle-xmark"></i></button>
                    </div>
                    <div className="modal-body">

                      <div>
                        <div className="container">
                          <div className="row">
                            {products.map((product, index) => (


                              <div key={index} className="col products d-flex align-items-center justify-content-center position-relative" id={product.saleFlowId} onClick={() => handleNewApplication(product.saleFlowId, isSimulation)}>
                                {product.saleFlowName}<br />
                                <div style={{ color: '#f00', fontSize: '10px', position: 'absolute', top: '50px' }}>{product.saleFlowId}</div>
                              </div>


                            ))}

                          </div>
                        </div>

                      </div>

                    </div>
                  </div>
                </div>

              </div>

              {renderRows() === null && <p style={{ textAlign: 'center', padding: "20px" }}>{layoutT('there_are_no_applications')}</p>}

              <div className="filters filters-bottom">

                <div className="pagination-left">
                  <div className='pagination'>
                    {renderPagination()}
                  </div>

                  <div className='pagination-select'>
                    <span className='filter-select applications-number'>
                      <select
                        id="itemsPerPage"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </span>
                    <label htmlFor="itemsPerPage">{layoutT('per_page')} </label>

                  </div>

                </div>

                <div className="filters-right">
                  <FilterSortMenu
                    showFilterMenu={showFilterMenuBottom}
                    onFilterButtonClick={handleFilterButtonClickBottom}
                    filterMenuRef={filterMenuBottomRef}
                    showSortMenu={showSortMenuBottom}
                    onSortButtonClick={handleSortButtonClickBottom}
                    sortMenuRef={sortMenuBottomRef}
                    statuses={statuses}
                    selectedFilters={selectedFilters}
                    onFilterOptionClick={handleFilterOptionClick}
                    onSortOptionClick={handleSortOptionClick}
                    orderBy={orderBy}
                    onClear={clearAll}
                    layoutT={layoutT}
                  />
                </div>

              </div>

            </div>
            <div className="application-buttons">
              <button
                className="mt-4 btn-purple delete"
                onClick={() => setShowDeleteModal(true)}
                disabled={selectedAppIds.length < 1}
              >
                {layoutT('delete')}
              </button>
              <a
                href="#"
                className={`mt-4 application-sale ${simulationInfo && !simulationInfo?.canCreateMore ? 'disabled-link' : ''}`}
                onClick={(e) => {
                  if (data.canCreateMore) {
                    e.preventDefault();
                  } else {
                    setSimulation(true);

                    if (isDebugMode()) {
                      setShowNewApplicationModal(true);
                    } else {
                      handleNewApplication(10, true);
                    }


                  }
                }}
              >
                {layoutT("simulate_a_sale")}
                {simulationInfo && (
                  !simulationInfo.isUnlimited && ` (${simulationInfo.maxCount - simulationInfo.currentCount})`
                )}
              </a>
            </div>
          </div>

        )}
      </div>
    </div>

  );
};
export default Applications;